import { Component, inject } from '@angular/core';
import { DiscountTypeEnum } from '../../../../enum/discount-type.enum';
import { CustomerState } from '../../../../stores/tunnel/model/customer-state';
import { Funding } from '../../../../stores/tunnel/model/funding';
import { FundingState } from '../../../../stores/tunnel/model/funding-state';
import { InstallationState } from '../../../../stores/tunnel/model/installation-state';
import { PricingState } from '../../../../stores/tunnel/model/pricing-state';
import {
  FidelityCardPointSimulationDto,
  FidelityCardTypeEnum,
  GetCustomerFidelityInfoInput,
} from '../../../../services/fidelity/fidelity';
import { TunnelDataStore } from '../../../../stores/tunnel/tunnel-data.store';
import { TunnelNavigationStore } from '../../../../stores/tunnel/tunnel-navigation.store';
import { MODAL, ModalService } from '../../../../services/modal.service';
import { StatisticService } from '../../../../services/statistic.service';
import { FidelityService } from '../../../../services/fidelity/fidelity.service';
import { StatisticTypeEnum } from '../../../../enum/statistic-type.enum';
import { CONFIG_CONTEXT } from '../../../../enum/config-context.enum';
import { PaytweakRequestInput } from '../../../../services/fidelity/paytweak';
import { BaseTunnelObject } from '../../model/base-tunnel-object';
import { BathroomService } from '../../../../services/configurators-2d/applications/bathroom.service';
import { BathroomConfigurationData } from '../../../../stores/tunnel/model/configuration-data/bathroom-configuration-data';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { FeatureSettingsService } from '../../../../services/feature-settings/feature-settings.service';

@Component({
  selector: 'ng-casto-bathroom-tunnel-validation',
  templateUrl: './bathroom-tunnel-validation.component.html',
})
export class BathroomTunnelValidationComponent extends BaseTunnelObject {
  // Services
  protected bathroomService = inject(BathroomService);
  private readonly modalService = inject(ModalService);
  private readonly statisticService = inject(StatisticService);
  private readonly fidelityService = inject(FidelityService);
  protected readonly featureSettingsService = inject(FeatureSettingsService);

  // Properties
  protected currentDiscount: DiscountTypeEnum;
  protected currentPricing: PricingState;
  protected currentInstallation: InstallationState;
  protected currentWithInstallation: boolean;
  protected currentFunding: FundingState;
  protected currentCustomer: CustomerState;
  protected loading: boolean = false;
  protected fidelityLoading: boolean = false;
  private bathroomConfiguration: BathroomConfigurationData;
  protected refreshLoading: boolean = false;
  protected readonly FidelityCardTypeEnum = FidelityCardTypeEnum;

  constructor() {
    const tunnelNavigationStore = inject(TunnelNavigationStore);
    const tunnelDataStore = inject(TunnelDataStore);
    super(tunnelNavigationStore, tunnelDataStore);

    this.tunnelNavigationStore = tunnelNavigationStore;
    this.tunnelDataStore = tunnelDataStore;
  }

  get disableInstallation(): boolean {
    return this.bathroomService.isInstallationDisabled();
  }

  get discountNumberValue(): number {
    switch (this.currentDiscount) {
      case DiscountTypeEnum.Discount0:
        return 0;
      case DiscountTypeEnum.Discount5:
        return 5;
      case DiscountTypeEnum.Discount10:
        return 10;
      case DiscountTypeEnum.Discount20:
        return 20;
    }
  }

  get currentFidelity(): FidelityCardPointSimulationDto {
    if (this.currentPricing?.fidelity) {
      return this.currentPricing.fidelity[this.currentWithInstallation ? 0 : 1];
    }
    return undefined;
  }

  get isCardExpired() {
    if (!this.currentCustomer.fidelity?.expiredDate) return false;
    let today = new Date();
    let dateToNotice = new Date();

    const nbDaysNoticeEndSubscriptionPaidCard = 30;
    //Ajout de la date de notification en fonction du setting
    dateToNotice.setTime(
      this.currentCustomer.fidelity.expiredDate.getTime() - nbDaysNoticeEndSubscriptionPaidCard * 24 * 60 * 60 * 1000
    );
    return today > dateToNotice;
  }

  get premiumCardEarnedCoupons(): number {
    let count = 0;
    this.currentFidelity?.premiumCardCoupons?.forEach(coupon => {
      count += coupon.count;
    });
    return count;
  }

  get displayDiscount(): boolean {
    return this.discountNumberValue > 0;
  }

  get funding(): Funding {
    return this.currentFunding[
      this.currentInstallation.withInstallation ? 'fundingWithInstallation' : 'fundingWithoutInstallation'
    ];
  }

  ngOnInit(): void {
    super.ngOnInit();
    //Envoi de la stat d'accès à la page de simulation de validation
    this.statisticService.sendStatistic(
      StatisticTypeEnum.VALIDATION_PER_PROJECT_COUNT,
      1,
      CONFIG_CONTEXT.BATHROOM_V2,
      this.bathroomConfiguration.configurationId
    );
    this.computeFidelityPoint().then();
  }

  private async computeFidelityPoint() {
    await this.bathroomService.computeFidelityPoint({
      amount: this.currentPricing.totalWithoutFunding.actualAmount,
      customerId: this.currentCustomer.userData.kfId,
    });
  }

  protected subscribeToObservable() {
    this.subscriptionList.push(
      this.tunnelDataStore.getCurrentPricing().subscribe(currentPricing => {
        if (currentPricing) {
          this.currentPricing = currentPricing;
        }
      }),
      this.tunnelDataStore.getCurrentDiscount().subscribe(async currentDiscount => {
        if (currentDiscount && this.currentDiscount !== currentDiscount) {
          this.currentDiscount = currentDiscount;
        }
      }),
      this.tunnelDataStore.getCurrentInstallation().subscribe(async currentInstallation => {
        if (currentInstallation) {
          this.currentInstallation = currentInstallation;
        }
      }),
      this.tunnelDataStore.getCurrentWithInstallation().subscribe(async currentWithInstallation => {
        if (currentWithInstallation !== undefined && this.currentWithInstallation !== currentWithInstallation) {
          let lastCurrentValue = this.currentWithInstallation;
          this.currentWithInstallation = currentWithInstallation;
          //Nathan - 08/07 : Ces lignes étaient commentées par Benjamin, surement pour essayer d'opti les appels
          if (lastCurrentValue !== null) {
            await this.updateFunding();
          }
        }
      }),
      this.tunnelDataStore.getCurrentFunding().subscribe(async currentFunding => {
        if (currentFunding) {
          this.currentFunding = currentFunding;
        }
      }),
      this.tunnelDataStore.getFidelityLoading().subscribe(async fidelityLoading => {
        this.fidelityLoading = fidelityLoading;
      }),
      this.tunnelDataStore.getCurrentCustomer().subscribe(async customer => {
        if (customer) {
          this.currentCustomer = customer;
        }
      }),
      this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
        if (configurationData) {
          this.bathroomConfiguration = configurationData.get(CONFIG_CONTEXT.BATHROOM_V2) as BathroomConfigurationData;
        }
      })
    );
  }

  protected async setWithInstallation(withInstallation: boolean) {
    await this.bathroomService.updateWithInstallation(withInstallation);
    this.tunnelDataStore.setWithInstallation(withInstallation);
  }

  openDetail() {
    this.modalService.display(MODAL.INSTALLATION_DETAIL, {
      installationDetails: this.currentInstallation.detailInstallation,
      bom: this.bathroomConfiguration?.bom,
    });
  }

  protected async updateFunding() {
    try {
      this.loading = true;
      await this.computeFunding();
    } catch (error) {
      this.loading = false;
    } finally {
      this.loading = false;
    }
  }

  private async computeFunding() {
    await this.bathroomService.computeFunding({
      tunnelConfigurationId: this.bathroomConfiguration.configurationId,
      downPaymentAmount: this.currentFunding.downPayment,
      shouldRecalculateFinancing: true,
    });
  }

  private async createFidelityCard(input: { withPremium: boolean }): Promise<void> {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    this.modalService.display(MODAL.CUSTOMER_INFO_FORM, {
      defaultData: {
        customer: this.currentCustomer.userData,
        address: this.currentCustomer.userData.address,
        marketingConsent: this.currentCustomer.userData.marketingConsent,
      },
      upgrade: input.withPremium,
      statisticType: input.withPremium
        ? StatisticTypeEnum.NEW_PREMIUM_CARD_VALIDATION
        : StatisticTypeEnum.NEW_FREE_CARD_VALIDATION,
    });
  }

  protected async createPremiumCard() {
    await this.createFidelityCard({
      withPremium: true,
    });
  }

  protected async createFreeCard() {
    await this.createFidelityCard({
      withPremium: false,
    });
  }

  private buildPaytweakRequestInput(): PaytweakRequestInput {
    let input: PaytweakRequestInput = {
      customerId: this.currentCustomer.userData.kfId,
      cardNumber: this.currentFidelity.cardNumber,
      firstname: this.currentCustomer.userData.firstname,
      lastname: this.currentCustomer.userData.lastname,
      address: this.currentCustomer.userData.address.line1,
      postalCode: this.currentCustomer.userData.address.postalCode,
      city: this.currentCustomer.userData.address.city,
      country: this.currentCustomer.userData.address.country,
      phone: this.currentCustomer.userData.phoneNumber,
      email: this.currentCustomer.userData.email,
    };

    return input;
  }

  private async showPaytweakQrCode() {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    let input: PaytweakRequestInput = this.buildPaytweakRequestInput();

    this.modalService.display(MODAL.FIDELITY_PAYMENT_QRCODE, {
      paytweakRequestInput: input,
      onCloseCallback: undefined,
    });
  }

  private async postRenewPremiumCard() {
    if (!(await this.featureSettingsService.isFidelityEnabled())) {
      return;
    }

    const input: GetCustomerFidelityInfoInput = {
      idKingfisher: this.currentCustomer.userData.kfId,
    };
    if (this.currentCustomer.userData.kfId != null) {
      try {
        await this.fidelityService.postRenewPremiumCard(input);
      } catch (error) {
        logError(error);
      } finally {
        await this.showPaytweakQrCode();
      }
    }
  }

  protected async updatePremiumCard() {
    await this.postRenewPremiumCard();
  }

  protected async getPaymentLink() {
    await this.showPaytweakQrCode();
  }

  protected async refreshFidelity() {
    try {
      this.refreshLoading = true;
      await this.tunnelDataStore.loadCurrentCustomer(this.currentCustomer.userData);
    } catch (error) {
      logError(error);
    } finally {
      this.refreshLoading = false;
    }
  }

  protected fidelityEnabled(): boolean {
    return this.featureSettingsService.fidelityEnabled;
  }

}
